/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import type { LoaderFunctionArgs } from "react-router-dom"
import { Route, createBrowserRouter, createRoutesFromElements, redirect } from 'react-router-dom'
import * as Views from './views'

function protectedLoader({ request }: LoaderFunctionArgs) {
  const isAuthenticated = true
  // If the user is not logged in and tries to access `/protected`, we redirect
  // them to `/login` with a `from` parameter that allows login to redirect back
  // to this page upon successful authentication
  if (!isAuthenticated) {
    const params = new URLSearchParams()
    params.set('from', new URL(request.url).pathname)
    return redirect('/login?' + params.toString())
  }

  return null
}

// https://github.com/remix-run/react-router/blob/dev/examples/auth-router-provider/src/App.tsx#L1
const routes = createRoutesFromElements(
  <Route path='/' errorElement={<Views.NotFound />}>
    <Route path='/logout' element={<Views.Logout />} />
    <Route path='/oauth2/callback' element={<Views.OAuthCallback />} />
    <Route path='/' element={<Views.Login />}>
      <Route loader={protectedLoader} id='Auditoria' path='audit' element={<Views.AuditList />} handle={{ crumb: true }} />
      <Route id='Informações Fiscais de Fornecedores' path='suppliers-tax-information' handle={{ crumb: true }}>
        <Route index element={<Views.SuppliersTaxInformationList />} />
        <Route id='Visualizar Informação Fiscal do Fornecedor' path=':id' element={<Views.SupplierTaxInformationDetail />} handle={{ crumb: true }} />
      </Route>
      <Route id='Produtos' path='products' handle={{ crumb: true }}>
        <Route index element={<Views.ProductList />} />
        <Route id='Detalhes do produto' path=':sku' element={<Views.ProductEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Postagens' path='posts' handle={{ crumb: true }}>
        <Route index element={<Views.PostList />} />
        <Route id='Cadastrar postagem' path='create' element={<Views.PostCreate />} handle={{ crumb: true }} />
        <Route id='Editar postagem' path=':id' element={<Views.PostEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Relatórios de Transparência' path='transparency-reports' handle={{ crumb: true }}>
        <Route index element={<Views.TransparencyReports />} />
        <Route id='Cadastrar relatório de transparência' path='create' element={<Views.TransparencyReportCreate />} handle={{ crumb: true }} />
        <Route id='Editar relatório de transparência' path=':id' element={<Views.TransparencyReportEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Lojas' path='stores' handle={{ crumb: true }}>
        <Route index element={<Views.StoreList />} />
        <Route id='Cadastrar loja' path='create' element={<Views.StoreCreate />} handle={{ crumb: true }} />
        <Route id='Editar loja' path=':id' element={<Views.StoreEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Usuários' path='users' handle={{ crumb: true }}>
        <Route index element={<Views.UserList />} />
        <Route id='Cadastrar usuário' path='create' element={<Views.UserCreate />} handle={{ crumb: true }} />
        <Route id='Editar usuário' path=':id' element={<Views.UserEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Feature Flags' path='feature-flags' element={<Views.FeatureFlagList />} handle={{ crumb: true }} />
      <Route id='Páginas' path='pages' handle={{ crumb: true }}>
        <Route index element={<Views.PageList />} />
        <Route id='Cadastrar página' path='create' element={<Views.PageCreate />} handle={{ crumb: true }} />
        <Route id='Editar página' path=':id' element={<Views.PageEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Landing Pages' path='landing-pages' handle={{ crumb: true }}>
        <Route index element={<Views.LandingPageList />} />
        <Route id='Criar landing page' path='create' element={<Views.LandingPageEditor />} handle={{ crumb: false, emptyLayout: true }} />
        <Route id='Métricas da landing page' path=':id/analytics' element={<Views.LandingPageAnalytics />} handle={{ crumb: true }} />
        <Route id='Editar landing page' path=':id' element={<Views.LandingPageEditor />} handle={{ crumb: false, emptyLayout: true }} />
      </Route>
      <Route id='Email Marketing' path='email-marketing' handle={{ crumb: true }}>
        <Route index element={<Views.EmailMarketingList />} />
        <Route id='Criar campanha de e-mail' path='create' element={<Views.EmailMarketingEditor />} handle={{ crumb: false, emptyLayout: true }} />
        <Route id='Editar campanha de e-mail' path=':id' element={<Views.EmailMarketingEditor />} handle={{ crumb: false, emptyLayout: true }} />
      </Route>
      <Route id='Dicas e Receitas' path='recipes' handle={{ crumb: true }}>
        <Route index element={<Views.RecipeList />} />
        <Route id='Editar dica ou receita' path=':id' element={<Views.RecipeEdit />} handle={{ crumb: true }} />
        <Route id='Cadastrar dica ou receita' path='create' element={<Views.RecipeEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Encartes' path='encartes' handle={{ crumb: true }}>
        <Route index element={<Views.EncarteList />} />
        <Route id='Cadastrar encarte' path='create' element={<Views.EncarteCreate />} handle={{ crumb: true }} />
        <Route id='Editar encarte' path=':id' element={<Views.EncarteEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Banners' path='banners' handle={{ crumb: true }}>
        <Route index element={<Views.BannerList />} />
        <Route id='Cadastrar banner' path='create' element={<Views.BannerCreate />} handle={{ crumb: true }} />
        <Route id='Editar banner' path=':id' element={<Views.BannerEdit />} handle={{ crumb: true }} />
        <Route path='locations'>
          <Route index element={<Views.BannerLocationsList />} />
          <Route id='Editar local de banner' path=':id' element={<Views.BannerLocationEdit />} handle={{ crumb: true }} />
          <Route id='Cadastrar local de banner' path='create' element={<Views.BannerLocationCreate />} handle={{ crumb: true }} />
        </Route>
      </Route>
      <Route id='Marcas' path='brands' handle={{ crumb: true }}>
        <Route index element={<Views.BrandList />} />
        <Route id='Cadastrar marca' path='create' element={<Views.BrandCreate />} handle={{ crumb: true }} />
        <Route id='Editar marca' path=':id' element={<Views.BrandEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Contato' path='contact-messages' handle={{ crumb: true }}>
        <Route index element={<Views.ContactMessageList />} />
        <Route id='Mensagens de contato' path=':id' element={<Views.ContactMessageDetail />} handle={{ crumb: true }} />
      </Route>
      <Route id='Vídeos' path='videos' handle={{ crumb: true }}>
        <Route index element={<Views.VideoList />} />
        <Route id='Cadastrar vídeo' path='create' element={<Views.VideoEdit />} handle={{ crumb: true }} />
        <Route id='Detalhes do vídeo' path=':id' element={<Views.VideoEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Trabalhe conosco' path='work-with-us' handle={{ crumb: true }}>
        <Route index element={<Views.WorkWithUsList />} />
        <Route id='Cadastro vaga' path='create' element={<Views.WorkWithUsCreate />} handle={{ crumb: true }} />
        <Route id='Detalhes da vaga' path=':id' handle={{ crumb: true }} element={<Views.WorkWithUsEdit />} />
        <Route id='Submissões de vagas de emprego' path='job-submissions'>
          <Route index element={<Views.WorkWithUsSubmissionsList />} handle={{ crumb: true }} />
          <Route id='Detalhe de submissão de vaga' path=':submissionId' element={<Views.WorkWithUsSubmissionJob />} handle={{ crumb: true }} />
        </Route>
      </Route>
      <Route id='Categorias de ofertas' path='offer-categories' handle={{ crumb: true }}>
        <Route index element={<Views.OfferCategoryList />} />
        <Route id='Editar categoria de oferta' path=':id' element={<Views.OfferCategoryEdit />} handle={{ crumb: true }} />
        <Route id='Cadastrar categoria de oferta' path='create' element={<Views.OfferCategoryCreate />} handle={{ crumb: true }} />
      </Route>
      <Route id='Ofertas' path='offers' handle={{ crumb: true }}>
        <Route index element={<Views.ProductOfferList />} />
        <Route id='Editar oferta' path=':id' element={<Views.ProductOfferEdit />} handle={{ crumb: true }} />
        <Route id='Cadastrar oferta' path='create' element={<Views.ProductOfferCreate />} handle={{ crumb: true }} />
      </Route>
      <Route id='Categorias de produtos' path='product-sections' handle={{ crumb: true }}>
        <Route index element={<Views.ProductSections />} />
        <Route id='Editar categoria de produto' path=':id' element={<Views.ProductSectionEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Subcategorias de produtos' path='product-categories' handle={{ crumb: true }}>
        <Route index element={<Views.ProductCategories />} />
      </Route>
      <Route id='Perguntas frequentes' path='frequently-asked-question' handle={{ crumb: true }}>
        <Route index element={<Views.FrequentlyAskedQuestionList />} />
        <Route id='Cadastrar pergunta frequente' path='create' element={<Views.FrequentlyAskedQuestionCreate />} handle={{ crumb: true }} />
        <Route id='Editar pergunta frequente' path=':id' element={<Views.FrequentlyAskedQuestionEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Postagens do instagram' path='instagram-posts' handle={{ crumb: true }}>
        <Route index element={<Views.InstagramPostsList />} />
        <Route id='Cadastrar postagem do instagram' path='create' element={<Views.InstagramPostCreate />} handle={{ crumb: true }} />
        <Route id='Editar postagem do instagram' path=':id' element={<Views.InstagramPostEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Workshops' path='workshops' handle={{ crumb: true }}>
        <Route index element={<Views.WorkshopsList />} />
        <Route id='Cadastrar eventos de workshop' path='create' element={<Views.WorkshopEventCreate />} handle={{ crumb: true }} />
        <Route id='Editar evento do workshop' path=':id' element={<Views.WorkshopEventEdit />} handle={{ crumb: true }} />
      </Route>
      <Route id='Relatórios' path='reports' handle={{ crumb: true }}>
        <Route index element={<Views.ReportList />} />
        <Route id='Detalhe do relatório' path=':id' element={<Views.ReportDetail />} handle={{ crumb: true }} />
      </Route>
      <Route path='job-requests' handle={{ crumb: true }}>
        <Route id='Requisições de abertura de vagas' index element={<Views.JobRequests />} />
        <Route id='Detalhe de solicitação de vaga' path=':id' element={<Views.JobRequestDetail />} handle={{ crumb: true }} />
        <Route id='Cadastrar solicitação de vaga' path='create' element={<Views.JobRequestCreate />} handle={{ crumb: true }} />
      </Route>
      <Route path='/visual-builder' element={<Views.VisualBuilder />} handle={{ crumb: false, emptyLayout: true }} />
      <Route path='/passkeys' element={<Views.Passkeys />} handle={{ crumb: false }} />
      <Route path='/advanced-actions' element={<Views.AdvancedActions />} handle={{ crumb: false }} />
      <Route id='Visão geral' index element={<Views.Overview />} handle={{ crumb: false }} />
    </Route>
  </Route>
)

export default createBrowserRouter(routes)
